/**
 * renderSiteClient — the client-side equivalent of workerd's renderSite: a whole
 * SiteDoc → the set of static files to publish (one HTML doc per page + Duro's
 * stylesheet), rendered in the browser via renderPage (real Duro components, no
 * server). This is the render half of "materialize client-side"; the buildSiteFiles
 * hookup that writes these into the git repo is the final wiring step.
 *
 * The text serializer (the existing 581-line Lexical→HTML) is taken as an option
 * so this stays free of it until that module is shared; the flow passes it in.
 */
import type {
  SiteDoc,
  Page,
  GridWidgetContent,
  GridItem,
  SiteThemeOrLegacy,
} from "@website-builder/schemas";
import { isSiteTheme } from "@website-builder/schemas";
import {
  resolveTokens,
  cssVarsToBlock,
  duro,
  fromLegacy,
  CURRENT_GENERATION_VERSION,
  type Tokens,
  type LegacyPreset,
  type LegacyKnobs,
} from "@website-builder/theme-engine";
import {
  renderPage,
  duroStylesheet,
  DURO_CSS_FILE,
  DS_CSS_FILE,
  type PageItem,
  type RenderRegion,
} from "./renderPage.js";
import { WB_RUNTIME_FILE, WB_RUNTIME_JS } from "./wbRuntime.js";
import type { DesignSystemModule } from "@website-builder/ds-contract";

export interface RenderedSiteFile {
  readonly path: string;
  readonly content: string;
}

export interface RenderSiteClientOptions {
  readonly serializeText?: (editorState: unknown) => string;
  /** Where the site root is served (default "/"). A preview passes its
   *  revision prefix, e.g. "/_preview/<site>/<rev>/"; it must end with "/". */
  readonly baseUrl?: string;
  /** The design system the widgets render with (default Duro). */
  readonly designSystem?: DesignSystemModule;
  /** An uploaded design system: its widgets' HTML, pre-rendered in the
   *  sandbox by widget id, and its compiled stylesheet (shipped as ds.css in
   *  place of duro.css). */
  readonly customDesignSystem?: {
    readonly html: ReadonlyMap<string, string>;
    readonly css: string;
  };
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>]/g, (c) =>
    c === "&" ? "&amp;" : c === "<" ? "&lt;" : "&gt;",
  );
}

export function slugToPath(slug: string): string {
  if (slug === "/") return "index.html";
  return slug.replace(/^\//, "") + "/index.html";
}

/** SiteDoc grid item → renderPage PageItem (recursive for nested grids). The
 *  mobile layout + hide flag ride on the serialized item out-of-band (added by
 *  serializeSiteDoc's buildGrid; not in the GridItem schema type). */
function toPageItem(gi: GridItem): PageItem {
  const resp = gi as unknown as {
    tablet?: { x: number; y: number; w: number; h: number };
    mobile?: { x: number; y: number; w: number; h: number };
    hiddenTablet?: boolean;
    hiddenMobile?: boolean;
  };
  const responsive = {
    ...(resp.tablet ? { tablet: resp.tablet } : {}),
    ...(resp.mobile ? { mobile: resp.mobile } : {}),
    ...(resp.hiddenTablet ? { hiddenTablet: true as const } : {}),
    ...(resp.hiddenMobile ? { hiddenMobile: true as const } : {}),
  };
  const content = gi.widget.content as { contentType: string } & Record<
    string,
    unknown
  >;
  // An Inline ("inline-grid") is a nested grid in its own cell: its blocks
  // flatten exactly like a section's, or the renderer meets them unflattened.
  if (content.contentType === "grid" || content.contentType === "inline-grid") {
    const g = content as unknown as GridWidgetContent;
    return {
      id: gi.widget.id,
      layout: gi.layout,
      ...responsive,
      contentType: content.contentType,
      content: {
        columns: g.columns,
        gapX: g.gapX,
        gapY: g.gapY,
        ...(g.rowHeight ? { rowHeight: g.rowHeight } : {}),
        ...(g.form ? { form: g.form } : {}),
        items: g.items.map(toPageItem),
      },
    };
  }
  return {
    id: gi.widget.id,
    layout: gi.layout,
    ...responsive,
    contentType: content.contentType,
    content,
  };
}

function resolveSiteTokens(theme: SiteThemeOrLegacy): Tokens {
  const siteTheme = isSiteTheme(theme)
    ? theme
    : {
        brand: fromLegacy(
          (theme as { preset: LegacyPreset }).preset,
          (theme as { knobs: LegacyKnobs }).knobs,
        ),
        generationVersion: CURRENT_GENERATION_VERSION,
      };
  return resolveTokens(duro, siteTheme).tokens;
}

/** The site's brand as `--wb-*` variables (for an uploaded design system). */
function siteThemeVarsCss(theme: SiteThemeOrLegacy): string {
  const siteTheme = isSiteTheme(theme)
    ? theme
    : {
        brand: fromLegacy(
          (theme as { preset: LegacyPreset }).preset,
          (theme as { knobs: LegacyKnobs }).knobs,
        ),
        generationVersion: CURRENT_GENERATION_VERSION,
      };
  return cssVarsToBlock(resolveTokens(duro, siteTheme).cssVars);
}

function buildNav(
  pages: readonly Page[],
  currentSlug: string,
  base = "/",
): string {
  const items = pages
    .map((p) => {
      const slug = p.data.slug;
      const cur = slug === currentSlug ? ' aria-current="page"' : "";
      // Slugs are site-root paths ("/", "/about"): re-root them under base.
      const href = `${base}${slug.replace(/^\//, "")}`;
      return `<li><a href="${href}"${cur}>${escapeHtml(p.data.title ?? slug)}</a></li>`;
    })
    .join("");
  return `<nav class="wb-nav"><ul>${items}</ul></nav>`;
}

/** A shared header/footer grid off the site → a renderPage region (or undefined). */
function toRegion(
  grid: GridWidgetContent | undefined,
): RenderRegion | undefined {
  if (!grid) return undefined;
  return {
    columns: grid.columns,
    gapX: grid.gapX,
    gapY: grid.gapY,
    ...(grid.rowHeight ? { rowHeight: grid.rowHeight } : {}),
    items: grid.items.map(toPageItem),
  };
}

export function renderSiteClient(
  doc: SiteDoc,
  opts: RenderSiteClientOptions = {},
): RenderedSiteFile[] {
  const tokens = resolveSiteTokens(doc.site.theme);
  // Shared header/footer — built once, identical on every page (the nav widget
  // inside gets this page's nav at render time).
  const site = doc.site as unknown as {
    header?: GridWidgetContent;
    footer?: GridWidgetContent;
  };
  const header = toRegion(site.header);
  const footer = toRegion(site.footer);
  const files: RenderedSiteFile[] = [];
  const custom = opts.customDesignSystem
    ? {
        html: opts.customDesignSystem.html,
        themeVarsCss: siteThemeVarsCss(doc.site.theme),
      }
    : undefined;
  for (const page of doc.pages) {
    const grid = page.data.grid as GridWidgetContent;
    const html = renderPage({
      title: page.data.title ?? "",
      columns: grid.columns,
      gapX: grid.gapX,
      gapY: grid.gapY,
      ...(grid.rowHeight ? { rowHeight: grid.rowHeight } : {}),
      items: grid.items.map(toPageItem),
      tokens,
      nav: buildNav(doc.pages, page.data.slug, opts.baseUrl),
      header,
      footer,
      serializeText: opts.serializeText,
      ...(opts.baseUrl ? { baseUrl: opts.baseUrl } : {}),
      ...(opts.designSystem ? { designSystem: opts.designSystem } : {}),
      ...(custom ? { customDesignSystem: custom } : {}),
    });
    files.push({ path: slugToPath(page.data.slug), content: html });
  }
  files.push(
    opts.customDesignSystem
      ? { path: DS_CSS_FILE, content: opts.customDesignSystem.css }
      : { path: DURO_CSS_FILE, content: duroStylesheet() },
  );
  // The audio-player hydration script (loaded `defer` only by pages that have an
  // audio widget). Served static by the materialize pipeline like duro.css.
  files.push({ path: WB_RUNTIME_FILE, content: WB_RUNTIME_JS });
  return files;
}
